<template>
  <div id="eligibility" data-cy="component-eligibility">
    <DebtorTags :has-codebtor="simulation.prospect.hasCodebtor" />
    <div v-if="policies && policies.length > 0">
      <div
        v-for="policy in policies"
        :key="policy.id"
        :data-cy="`plcy-${policy.id}`"
        class="bordered-bottom-box mb-1">
        <div class="eligibility-row">
          <a class="eligibility-label mr-2" @click="toggleComment(policy.id)">
            <b-icon :icon="policy.showComment ? 'angle-down' : 'angle-right'" />
            <label class="label">{{ policy.label }}</label>
          </a>
          <div class="eligibility-options">
            <PolicyRadioButton
              v-for="option in radioOptions"
              :key="`policy-${policy.id}-option-${option.value}`"
              v-model="policy.value"
              :option="option"
              :name="`name-${policy.id}`"
              @input="showComment(policy.id)" />
          </div>
        </div>
        <b-collapse :open="policy.showComment">
          <div class="comments my-1 ml-5 is-clearfix">
            <b-input
              v-model="policy.comment"
              :value="policy.comment==='Evaluación automática'?'':policy.comment"
              type="textarea"
              placeholder="Agrega un comentario" />
          </div>
        </b-collapse>
      </div>
    </div>
    <div v-else> No hay criterios de elegibilidad para la evaluación</div>
  </div>
</template>
<script>
import { EligibilityClient } from '@/application/services/evaluations/apis/eligibility-client';
import { mapMutations, mapState } from 'vuex';
import { EligibilityService } from '@/application/services/evaluations/eligibility-service';
import PolicyRadioButton from '@/ui/components/internal/evaluations/verify/PolicyRadioButton.vue';
import { eligibilityBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';
import DebtorTags from '@/ui/components/internal/evaluations/verify/DebtorTags.vue';

export default {
  name: 'EligibilityCriteria',
  components: {
    PolicyRadioButton,
    DebtorTags
  },
  props: {
    showTextComment: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      policies: [],
      radioOptions: [
        { label: 'No cumple', value: 'Refused', type: 'is-danger' },
        { label: 'Revisión', value: 'Reviewable', type: 'is-warning' },
        { label: 'Cumple', value: 'Approved', type: 'is-success' }
      ],
      eligibilityService: new EligibilityService(
        this.$store,
        new EligibilityClient(eligibilityBackend, authenticationService)
      )
    };
  },
  computed: {
    ...mapState('evaluation', ['manualEvaluationId', 'eligibilityData', 'simulation', 'eligibility', 'idNumber', 'eligibilityContext'])
  },
  watch: {
    policies: {
      handler(after, before) {
        if (!after || !before) return;
        if (after.length === 0 || before.length === 0) {
          return;
        }
        this.updateEligibilityResult();
      },
      deep: true
    }
  },
  async mounted() {
    await this.getEligibility();
  },
  methods: {
    ...mapMutations('evaluation', ['setEligibilityResult']),
    getPolicyById(id) {
      return this.policies.find(r => r.id === id);
    },
    showComment(id) {
      const policy = this.getPolicyById(id);
      policy.showComment = !!policy.value;
    },
    toggleComment(id) {
      const policy = this.getPolicyById(id);
      if (policy.value) {
        policy.showComment = !policy.showComment;
      }
    },
    async getEligibility() {
      await this.eligibilityService.getEligibilityForLabels(this.manualEvaluationId || this.$route.params.evaluationId);
      this.policies = this.eligibilityService.getEligibilityList(this.eligibilityData);
      this.updateEligibilityResult();
      const filter = this.filterAndMapPolicies(this.eligibility, this.idNumber);
      if (filter.length > 0) {
        this.policies = filter;
      }
    },
    filterAndMapPolicies(eligibility, idNumber) {
      if (eligibility && eligibility.calculate) {
        const resultEligibilityDebtor = eligibility.calculate.find(
          rule => rule.type === 'DEBTOR' && idNumber === rule.idNumber
        );
        const eligibilityResult = resultEligibilityDebtor.result;
        return this.policies.map(policy => {
          const newPolicy = eligibilityResult[policy.ruleName];
          return {
            ...policy,
            value: newPolicy.result,
            comment: newPolicy.reason === 'Evaluación automática' ? '' : newPolicy.reason
          };
        });
      }
      return [];
    },
    policiesEligibilityResult() {
      const result = {};
      if (!this.policies || this.policies.length === 0) return result;
      this.policies.forEach(policy => {
        result[policy.ruleName] = {
          result: policy.value,
          reason: policy.comment
        };
      });
      return result;
    },
    updateEligibilityResult() {
      const finalEligibility = { ...this.eligibility, context: this.eligibilityContext };
      finalEligibility.calculate = [
        {
          type: 'DEBTOR',
          idNumber: this.idNumber,
          result: this.policiesEligibilityResult()
        }
      ];
      this.setEligibilityResult(finalEligibility);
    }
  }
};
</script>
<style scoped>
.income-tag {
  width: 140px;
}

.bordered-bottom-box {
  border-bottom: 1px solid #e7e7e7;
  padding: 1rem 0;
  margin-bottom: 1rem;
}

.bordered-bottom-box:last-child {
  border-bottom: none;
}

.eligibility-row {
  justify-content: space-between;
}

.eligibility-row,
.eligibility-label,
.eligibility-options {
  display: flex;
  align-items: center;
}

.eligibility-label label {
  font-weight: 400;
  font-size: 0.95rem;
}

.eligibility-label .icon {
  color: var(--primary);
}

@media only screen and (max-width: 768px) {
  .box {
    padding: .8rem;
  }

  .eligibility-label span {
    margin-right: 10px;
  }

  .eligibility-label label {
    line-height: 1.2;
  }

  .eligibility-row,
  .eligibility-options {
    flex-direction: column;
  }

  .eligibility-label,
  .eligibility-options {
    align-items: flex-start;
    width: 100%;
  }

  .eligibility-options {
    margin-left: 1rem;
  }

  .eligibility-options label {
    margin-bottom: 5px;
  }

  .eligibility-label {
    margin-bottom: 1rem;
  }

}
</style>
<style>
#eligibility .comments .textarea {
  min-height: 3em;
  font-size: 0.85rem;
}

#eligibility .eligibility-options .b-radio.radio {
  color: #828282;
}

#eligibility .eligibility-label {
  color: #4D4D4D;
}

#eligibility .eligibility-options .b-radio.radio input[type=radio] + .check {
  border-width: 1px;
  border-color: #E6EAF1
}

#eligibility .eligibility-options .b-radio.radio input[type=radio]:checked + .check {
  border-width: 4px;
}

#eligibility .eligibility-options .b-radio.radio input[type=radio]:checked + .check.is-success {
  border-color: #00CCD1;
}

#eligibility .eligibility-options .b-radio.radio input[type=radio]:checked + .check.is-warning {
  border-color: #ffe08a;
}

#eligibility .eligibility-options .b-radio.radio input[type=radio]:checked + .check.is-danger {
  border-color: #E9007F;
}

#eligibility .eligibility-options .b-radio.radio input[type=radio] + .check:before {
  background: transparent;
}
</style>
